import { useEffect, useState } from 'react'
import { useRevealObserver } from '../hooks/useRevealObserver'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import MediaImage from './MediaImage'
import SocialIcon, { socialIconName } from './SocialIcon'
import {
  footerCols,
  issueDate,
  logoSrc,
  navLinks,
  searchStories,
  socials,
  type NavLink as NavLinkItem,
} from '../data/content'
import { affiliateRelForHref } from '../lib/affiliate'
import { publicPath, storyPath } from '../lib/paths'
import { ensureCmsCards, useCmsVersion } from '../cms/store'

const navLinkClass = (isActive: boolean) =>
  `inline-flex items-center gap-1 border-b pb-[3px] text-[12px] font-medium tracking-[0.16em] uppercase ${
    isActive ? 'border-rose text-rose' : 'border-transparent hover:border-rose hover:text-rose'
  }`

function ChevronDown() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="mt-px shrink-0"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function NavItem({ link, pathname }: { link: NavLinkItem; pathname: string }) {
  if (!link.children?.length) {
    return (
      <NavLink to={link.href} className={({ isActive }) => navLinkClass(isActive)}>
        {link.label}
      </NavLink>
    )
  }

  const isGroupActive =
    pathname === link.href || link.children.some((child) => pathname === child.href)

  return (
    <div className="group relative inline-flex items-center">
      <NavLink to={link.href} className={() => navLinkClass(isGroupActive)}>
        {link.label}
        <ChevronDown />
      </NavLink>
      <div className="invisible absolute top-full left-0 z-20 min-w-[168px] pt-2 opacity-0 transition-opacity duration-300 ease-in group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="border border-rule bg-paper pt-1">
        {link.children.map((child) => (
          <NavLink
            key={child.href}
            to={child.href}
            className={({ isActive }) =>
              `block px-4 py-2.5 text-[11px] font-medium tracking-[0.16em] uppercase ${
                isActive ? 'text-rose' : 'text-ink hover:text-rose'
              }`
            }
          >
            {child.label}
          </NavLink>
        ))}
        </div>
      </div>
    </div>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4.3-4.3" />
    </svg>
  )
}

function MenuIcon({ open }: { open: boolean }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  )
}

export default function Layout() {
  useCmsVersion()
  const [searchOpen, setSearchOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [query, setQuery] = useState('')
  const location = useLocation()

  useRevealObserver(location.pathname)

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return
    const path = `${location.pathname}${location.search}`
    fetch('/api/public/view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ path, referrer: document.referrer }),
      keepalive: true,
    }).catch(() => {})
  }, [location.pathname, location.search])

  useEffect(() => {
    setMenuOpen(false)
    setSearchOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const titles: Record<string, string> = {
      '/': 'TIGG | The It Girl Guide',
      '/look-books': 'Look Books | TIGG',
      '/trends': 'Trends | TIGG',
      '/2dos': '2Dos | TIGG',
      '/travel': 'Travel | TIGG',
      '/food': 'Food | TIGG',
      '/beauty': 'Beauty | TIGG',
      '/wellness': 'Health and Wellness | TIGG',
      '/stories': 'All stories | TIGG',
      '/closet': 'Shop my closet | TIGG',
      '/about': 'About Sky Haarsma | TIGG',
      '/newsletter': 'Newsletter | TIGG',
      '/contact': 'Contact | TIGG',
      '/contributors': 'Contributors | TIGG',
      '/press': 'Press | TIGG',
      '/terms': 'Terms of use | TIGG',
      '/privacy': 'Privacy policy | TIGG',
    }
    if (location.pathname.startsWith('/stories/')) return
    if (location.pathname.startsWith('/author/')) return
    document.title = titles[location.pathname] ?? 'TIGG | The It Girl Guide'
  }, [location.pathname])

  useEffect(() => {
    if (!searchOpen) return
    void ensureCmsCards()
  }, [searchOpen])

  useEffect(() => {
    if (!searchOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSearchOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [searchOpen])

  const results = searchStories(query)

  return (
    <div className="min-h-svh min-w-0 overflow-x-clip bg-paper text-ink">
      <ScrollToTop />
      <div className="mx-auto min-w-0 max-w-[1440px] px-[clamp(16px,3vw,40px)]">
        <header className="sticky top-0 z-40 -mx-[clamp(16px,3vw,40px)] min-w-0 bg-paper px-[clamp(16px,3vw,40px)]">
          <div className="flex min-w-0 items-center justify-between gap-3 border-b border-rule py-[22px] pb-4 sm:gap-6">
          <Link to="/" aria-label="TIGG home" className="shrink-0">
            <img
              src={logoSrc}
              alt="The It Girl Guide"
              width={160}
              height={42}
              className="block h-[36px] w-auto sm:h-[42px]"
            />
          </Link>

          <nav className="hidden min-w-0 flex-wrap items-center gap-[clamp(14px,2.4vw,36px)] lg:flex" aria-label="Primary">
            {navLinks.map((link) => (
              <NavItem key={link.href} link={link} pathname={location.pathname} />
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label="Search"
              onClick={() => {
                setSearchOpen(true)
                setMenuOpen(false)
              }}
              className="grid size-11 place-items-center border border-rule bg-transparent text-ink hover:border-rose hover:text-rose"
            >
              <SearchIcon />
            </button>
            <Link
              to={publicPath('/newsletter')}
              className="hidden h-11 items-center bg-ink px-[18px] text-[11px] tracking-[0.16em] text-white uppercase hover:bg-rose sm:inline-flex"
            >
              Subscribe
            </Link>
            <button
              type="button"
              className="grid size-11 place-items-center border border-rule lg:hidden"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <MenuIcon open={menuOpen} />
            </button>
          </div>
          </div>

        {menuOpen && (
          <nav className="flex flex-col gap-4 border-b border-rule py-5 lg:hidden" aria-label="Mobile">
            {navLinks.map((link) =>
              link.children?.length ? (
                <div key={link.href} className="flex flex-col gap-2">
                  <NavLink
                    to={link.href}
                    className={({ isActive }) =>
                      `text-[12px] font-medium tracking-[0.16em] uppercase ${isActive ? 'text-rose' : ''}`
                    }
                  >
                    {link.label}
                  </NavLink>
                  <div className="ml-3 flex flex-col gap-2 border-l border-rule pl-3">
                    {link.children.map((child) => (
                      <NavLink
                        key={child.href}
                        to={child.href}
                        className={({ isActive }) =>
                          `text-[11px] font-medium tracking-[0.16em] uppercase ${
                            isActive ? 'text-rose' : 'text-muted'
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.href}
                  to={link.href}
                  className="text-[12px] font-medium tracking-[0.16em] uppercase"
                >
                  {link.label}
                </NavLink>
              ),
            )}
            <Link to={publicPath('/newsletter')} className="text-[12px] font-medium tracking-[0.16em] uppercase">
              Subscribe
            </Link>
          </nav>
        )}
        </header>

        <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 text-[10px] tracking-[0.14em] text-muted uppercase sm:tracking-[0.2em]">
          <span className="min-w-0">Your digital stylist and new bff</span>
          <span className="shrink-0">{issueDate()}</span>
        </div>

        <Outlet />

        <footer data-reveal className="mt-10 grid grid-cols-1 gap-[clamp(24px,3vw,44px)] border-t border-rule py-[clamp(32px,4vw,52px)] pb-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-4">
            <Link to="/" aria-label="TIGG home" className="shrink-0 self-start">
              <img
                src={logoSrc}
                alt="The It Girl Guide"
                width={160}
                height={38}
                loading="lazy"
                decoding="async"
                className="block h-[38px] w-auto object-contain object-left"
              />
            </Link>
            <p className="max-w-[30ch] leading-relaxed text-muted">
              your big yellow book on how to be that it-girl.
            </p>
            <div className="flex items-center gap-1">
              {socials.map((social) => (
                <a
                  key={social.short}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grid size-11 place-items-center text-ink hover:text-rose"
                >
                  <SocialIcon name={socialIconName(social.label)} />
                </a>
              ))}
            </div>
          </div>
          {footerCols.map((col) => (
            <div key={col.head} className="flex flex-col gap-3">
              <span className="border-b border-rule pb-2.5 text-[10px] tracking-[0.2em] text-muted uppercase">
                {col.head}
              </span>
              {col.links.map((link) => {
                const className = 'text-[13px] text-copy hover:text-rose'
                if (link.href.startsWith('https://theitgirlguide.com/')) {
                  return (
                    <a key={link.label} href={link.href} className={className}>
                      {link.label}
                    </a>
                  )
                }
                if (link.href.startsWith('http')) {
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel={affiliateRelForHref(link.href) ?? 'noreferrer'}
                      className={className}
                    >
                      {link.label}
                    </a>
                  )
                }
                return (
                  <Link key={link.label} to={link.href} className={className}>
                    {link.label}
                  </Link>
                )
              })}
            </div>
          ))}
        </footer>

        <div className="flex flex-wrap justify-between gap-3.5 py-[18px] pb-9 text-[10px] tracking-[0.14em] text-stone uppercase">
          <span>Copyright © {new Date().getFullYear()} The It Girl Guide</span>
          <span className="flex gap-3">
            <Link to={publicPath('/terms')} className="inline-flex min-h-11 items-center text-stone hover:text-rose">
              Terms
            </Link>
            <Link to={publicPath('/privacy')} className="inline-flex min-h-11 items-center text-stone hover:text-rose">
              Privacy
            </Link>
            <Link to="/admin/login" className="inline-flex min-h-11 items-center text-stone hover:text-rose">
              Login
            </Link>
          </span>
        </div>
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-paper/95 backdrop-blur-sm">
          <div className="mx-auto max-w-[720px] px-6 pt-16">
            <div className="mb-8 flex items-center justify-between">
              <p className="text-[10px] tracking-[0.2em] text-muted uppercase">Search TIGG</p>
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="text-[11px] tracking-[0.16em] uppercase hover:text-rose"
              >
                Close
              </button>
            </div>
            <label className="block">
              <span className="sr-only">Search TIGG</span>
              <input
                autoFocus
                type="search"
                name="q"
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="look books, siedrés, miami…"
                className="w-full min-w-0 border-0 border-b border-ink bg-transparent py-3 font-serif text-[clamp(22px,6vw,48px)] text-ink outline-none placeholder:text-rule"
              />
            </label>
            <ul className="mt-8 divide-y divide-rule">
              {results.map((story) => (
                <li key={story.slug}>
                  <Link
                    to={storyPath(story.slug)}
                    className="flex items-start gap-4 py-4 hover:text-rose"
                    onClick={() => setSearchOpen(false)}
                  >
                    {story.image ? (
                      <MediaImage src={story.image} size="thumb" alt={story.imageAlt || story.title} className="size-16 object-cover object-top" />
                    ) : (
                      <span className="size-16 bg-sand" />
                    )}
                    <span>
                      <span className="block text-[10px] tracking-[0.18em] text-rose uppercase">
                        {story.category}
                      </span>
                      <span className="mt-1 block font-serif text-[22px] leading-tight">{story.title}</span>
                    </span>
                  </Link>
                </li>
              ))}
              {query && results.length === 0 && (
                <li className="py-8 text-sm text-muted">nothing here — try another word.</li>
              )}
            </ul>
          </div>
        </div>
      )}

    </div>
  )
}
